import React, { useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  Loader2,
  Plus,
  Save,
  Settings,
  Trash2
} from 'lucide-react';

const inputClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400';
const textareaClass = `${inputClass} min-h-[96px] resize-y leading-6`;

const normalizeText = (value) => (value === null || value === undefined ? '' : String(value));
const compact = (value) => normalizeText(value).trim();

export const asJobProfileArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return [value].filter(Boolean);
};

export const isEmptyJobProfileAnswer = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

export const getJobObjectListFields = (question) => (
  Array.isArray(question?.validationRules?.fields) && question.validationRules.fields.length
    ? question.validationRules.fields
    : ['name', 'requirementType', 'context']
);

export const createJobObjectListItem = (question) => (
  getJobObjectListFields(question).reduce((acc, field) => ({ ...acc, [field]: '' }), {})
);

export const fieldLabel = (value) => (
  normalizeText(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

export const normalizeJobObjectListValue = (question, rawValue) => {
  if (Array.isArray(rawValue)) return rawValue;
  if (!compact(rawValue)) return [];

  try {
    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

export const cleanJobObjectListValue = (question, rawValue) => {
  const fields = getJobObjectListFields(question);
  const items = normalizeJobObjectListValue(question, rawValue);

  return items
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    .map((item) => fields.reduce((acc, field) => {
      const cleaned = typeof item[field] === 'boolean' ? item[field] : compact(item[field]);
      return cleaned !== '' ? { ...acc, [field]: cleaned } : acc;
    }, {}))
    .filter((item) => Object.keys(item).length > 0);
};

export const jobQuestionInitialValue = (question) => {
  const value = question?.answer?.value;
  if (value === null || value === undefined) return '';
  if (question.answerType === 'object_list' && Array.isArray(value)) return value;
  if (question.answerType === 'text_array' && Array.isArray(value)) return value.join('\n');
  return value;
};

export const toJobProfileAnswerValue = (question, rawValue) => {
  if (isEmptyJobProfileAnswer(rawValue)) return null;
  if (question.answerType === 'number' || question.answerType === 'scale') return Number(rawValue);
  if (question.answerType === 'boolean') return Boolean(rawValue);
  if (question.answerType === 'multi_select' || question.answerType === 'ranking') return asJobProfileArray(rawValue);
  if (question.answerType === 'text_array') {
    return Array.isArray(rawValue)
      ? rawValue.map(compact).filter(Boolean)
      : String(rawValue).split(/\n|,/).map(compact).filter(Boolean);
  }
  if (question.answerType === 'object_list') {
    const cleaned = cleanJobObjectListValue(question, rawValue);
    if (cleaned.length) return cleaned;
    try {
      const parsed = JSON.parse(rawValue);
      const parsedCleaned = cleanJobObjectListValue(question, parsed);
      return parsedCleaned.length ? parsedCleaned : null;
    } catch (_error) {
      return null;
    }
  }
  return typeof rawValue === 'string' ? rawValue.trim() : rawValue;
};

export const isJobProfileQuestionAnswered = (question) => Boolean(question?.answer && !question.answer.skipped);

const booleanObjectFields = new Set([
  'professionalUsageRequired',
  'portfolioRequired',
  'certificationAccepted',
  'repositoryRequired',
  'caseStudyRequired',
  'outcomeEvidenceRequired',
  'researchRequired',
  'publicationRequired',
  'thesisOrDissertationRelevant',
  'transcriptRequired',
  'acceptsEquivalentExperience',
  'budgetOwnershipRequired',
  'authorizationRequired',
  'sponsorshipAvailable',
  'relocationSupported'
]);
const isLongObjectField = (field) => /context|notes|details|description|stakeholders|skillsUsed|requiredSkills|requiredTechnologies|methodsRequired|outputTypes|regulatedMarkets|operatingCountries|accreditationRequired/i.test(field);
const isBooleanObjectField = (field) => booleanObjectFields.has(field) || /(Available|Supported|Accepted|Relevant)$/i.test(field);
const isNumberObjectField = (field) => /years|months|gpa|gpaScale|globalRank|percentile|teamSize|budgetAmount|score|rank/i.test(field);

const getQuestionStatus = (question, {
  answers,
  skippedAnswers,
  dirtyQuestionKeys,
  recentlySavedQuestionKeys,
  savingQuestionKey
}) => {
  if (!question) return 'empty';

  const key = question.questionKey;
  const skipped = Boolean(skippedAnswers[key]);
  const draftValue = toJobProfileAnswerValue(question, answers[key]);

  if (savingQuestionKey === key) return 'saving';
  if (dirtyQuestionKeys[key]) return skipped || draftValue !== null ? 'draft' : 'empty';
  if (skipped || question.answer?.skipped) return 'skipped';
  if (recentlySavedQuestionKeys[key] || question.answer) return 'saved';
  if (draftValue !== null) return 'draft';
  return 'empty';
};

const JobProfileQuestionStepper = ({
  profileSetup,
  answers,
  skippedAnswers,
  errors,
  activeIndex,
  finished,
  savingQuestionKey,
  dirtyQuestionKeys,
  recentlySavedQuestionKeys,
  onAnswerChange,
  onSkipChange,
  onActiveIndexChange,
  onFinishedChange,
  onSaveQuestion,
  title = 'Matching profile answers',
  description = 'Add structured job-side facts that improve deterministic matching and explanations.',
  emptyLabel = 'No job profile question bank is available from the backend.',
  className = ''
}) => {
  const questions = profileSetup?.questions || [];
  const progress = profileSetup?.questionProgress || {};
  const questionCount = questions.length;
  const safeIndex = questionCount ? Math.min(Math.max(activeIndex, 0), questionCount - 1) : 0;
  const currentQuestion = questions[safeIndex] || null;
  const currentKey = currentQuestion?.questionKey || '';
  const currentSection = currentQuestion
    ? fieldLabel(currentQuestion.section || currentQuestion.category || 'Matching profile')
    : 'Matching Profile';
  const currentStatus = getQuestionStatus(currentQuestion, {
    answers,
    skippedAnswers,
    dirtyQuestionKeys,
    recentlySavedQuestionKeys,
    savingQuestionKey
  });
  const currentIsSaving = savingQuestionKey === currentKey;
  const hasPrevious = safeIndex > 0;
  const hasNext = safeIndex < questionCount - 1;
  const showFinished = finished;

  useEffect(() => {
    if (!questionCount) return;
    if (activeIndex < 0 || activeIndex >= questionCount) {
      onActiveIndexChange(Math.min(Math.max(activeIndex, 0), questionCount - 1));
    }
  }, [activeIndex, onActiveIndexChange, questionCount]);

  const goToQuestion = (index) => {
    if (!questionCount || savingQuestionKey) return;
    const nextIndex = Math.min(Math.max(index, 0), questionCount - 1);
    onFinishedChange(false);
    onActiveIndexChange(nextIndex);
  };

  const renderQuestionInput = (question) => {
    const disabled = Boolean(skippedAnswers[question.questionKey]) || savingQuestionKey === question.questionKey;
    const value = answers[question.questionKey] ?? '';
    const choices = Array.isArray(question.choices) ? question.choices : [];
    const setValue = (nextValue) => onAnswerChange(question.questionKey, nextValue);

    if (question.answerType === 'boolean' || question.inputType === 'toggle') {
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            { label: 'Yes', value: true },
            { label: 'No', value: false }
          ].map((option) => {
            const checked = value === option.value;
            return (
              <label
                key={option.label}
                className={`flex min-h-10 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  checked ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600'
                } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-sky-200'}`}
              >
                <input
                  type="radio"
                  name={question.questionKey}
                  className="shrink-0 border-slate-300 text-sky-600 focus:ring-sky-500"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => setValue(option.value)}
                />
                {option.label}
              </label>
            );
          })}
        </div>
      );
    }

    if (question.answerType === 'single_select') {
      return (
        <select disabled={disabled} value={value || ''} onChange={(event) => setValue(event.target.value)} className={inputClass}>
          <option value="">Select an answer</option>
          {choices.map((choice) => <option key={choice.value} value={choice.value}>{choice.label || choice.value}</option>)}
        </select>
      );
    }

    if (question.answerType === 'multi_select' || question.answerType === 'ranking' || question.inputType === 'checkbox') {
      const selected = asJobProfileArray(value);
      const maxItems = Number(question.validationRules?.maxItems || 0);
      return (
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))' }}
        >
          {choices.map((choice) => {
            const checked = selected.includes(choice.value);
            const selectedIndex = selected.indexOf(choice.value);
            return (
              <label
                key={choice.value}
                className={`flex min-h-10 min-w-0 items-start gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  checked ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-200 bg-white text-slate-600'
                } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:border-sky-200'}`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => {
                    if (!checked && maxItems && selected.length >= maxItems) return;
                    setValue(checked ? selected.filter((item) => item !== choice.value) : [...selected, choice.value]);
                  }}
                />
                <span className="min-w-0 flex-1 whitespace-normal break-words leading-5">
                  {choice.label || choice.value}
                </span>
                {question.answerType === 'ranking' && checked && (
                  <span className="shrink-0 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">
                    {selectedIndex + 1}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      );
    }

    if (question.answerType === 'number' || question.answerType === 'scale') {
      return (
        <input
          type="number"
          disabled={disabled}
          value={value}
          min={question.validationRules?.min}
          max={question.validationRules?.max}
          onChange={(event) => setValue(event.target.value)}
          className={inputClass}
        />
      );
    }

    if (question.answerType === 'object_list') {
      const fields = getJobObjectListFields(question);
      const items = normalizeJobObjectListValue(question, value);
      const visibleItems = items.length ? items : [createJobObjectListItem(question)];
      const maxItems = Number(question.validationRules?.maxItems || 0);
      const itemName = question.category || 'requirement';
      const updateObjectItem = (index, field, nextFieldValue) => {
        const nextItems = visibleItems.map((item, itemIndex) => (
          itemIndex === index ? { ...item, [field]: nextFieldValue } : item
        ));
        setValue(nextItems);
      };
      const addObjectItem = () => {
        if (maxItems && visibleItems.length >= maxItems) return;
        setValue([...visibleItems, createJobObjectListItem(question)]);
      };
      const removeObjectItem = (index) => {
        const nextItems = visibleItems.filter((_, itemIndex) => itemIndex !== index);
        setValue(nextItems.length ? nextItems : [createJobObjectListItem(question)]);
      };

      return (
        <div className="space-y-3">
          {visibleItems.map((item, index) => (
            <div key={`${question.questionKey}-${index}`} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                  {fieldLabel(itemName)} {index + 1}
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => removeObjectItem(index)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label={`Remove ${itemName}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))' }}
              >
                {fields.map((field) => {
	                  const fieldValue = item[field];
	                  const label = fieldLabel(field);
	                  const longField = isLongObjectField(field);
	                  const booleanField = isBooleanObjectField(field);
	                  const numberField = isNumberObjectField(field);
	                  const fieldOptions = Array.isArray(question.validationRules?.fieldOptions?.[field])
	                    ? question.validationRules.fieldOptions[field]
	                    : [];
	                  const fieldHelp = question.validationRules?.fieldHelp?.[field];

	                  return (
	                    <label key={field} className={longField ? 'min-w-0 sm:col-span-2' : 'min-w-0'}>
	                      <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
	                        {label}
	                      </span>
	                      {fieldOptions.length ? (
	                        <select
	                          disabled={disabled}
	                          value={normalizeText(fieldValue)}
	                          onChange={(event) => updateObjectItem(index, field, event.target.value)}
	                          className={inputClass}
	                        >
	                          <option value="">Not specified</option>
	                          {fieldOptions.map((option) => (
	                            <option key={option.value || option} value={option.value || option}>
	                              {option.label || option.value || option}
	                            </option>
	                          ))}
	                        </select>
	                      ) : booleanField ? (
	                        <select
	                          disabled={disabled}
	                          value={fieldValue === true ? 'true' : fieldValue === false ? 'false' : normalizeText(fieldValue)}
	                          onChange={(event) => updateObjectItem(index, field, event.target.value)}
                          className={inputClass}
                        >
                          <option value="">Not specified</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      ) : longField ? (
                        <textarea
                          rows={2}
                          disabled={disabled}
                          value={normalizeText(fieldValue)}
                          onChange={(event) => updateObjectItem(index, field, event.target.value)}
                          className={textareaClass}
                        />
                      ) : (
                        <input
                          type={numberField ? 'number' : 'text'}
                          disabled={disabled}
                          value={normalizeText(fieldValue)}
                          onChange={(event) => updateObjectItem(index, field, event.target.value)}
	                          className={inputClass}
	                        />
	                      )}
	                      {fieldHelp && <span className="mt-1 block text-xs leading-5 text-slate-500">{fieldHelp}</span>}
	                    </label>
	                  );
	                })}
              </div>
            </div>
          ))}
          <button
            type="button"
            disabled={disabled || (maxItems && visibleItems.length >= maxItems)}
            onClick={addObjectItem}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={15} /> Add {fieldLabel(itemName).toLowerCase()}
          </button>
        </div>
      );
    }

    if (question.answerType === 'textarea' || question.inputType === 'textarea' || question.answerType === 'text_array') {
      return (
        <textarea
          rows={3}
          disabled={disabled}
          value={Array.isArray(value) ? value.join('\n') : value}
          onChange={(event) => setValue(event.target.value)}
          className={textareaClass}
        />
      );
    }

    return <input type="text" disabled={disabled} value={value} onChange={(event) => setValue(event.target.value)} className={inputClass} />;
  };

  if (!questionCount) {
    return (
      <section className={`rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)] ${className}`}>
        <div className="border-b border-slate-100 p-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-sky-700">
              <Settings size={16} />
            </span>
            <div>
              <h2 className="font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{title}</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            {emptyLabel}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`rounded-lg border border-slate-200 bg-white shadow-[0_12px_30px_rgba(15,23,42,0.035)] ${className}`}>
      <div className="border-b border-slate-100 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#eef9ff] text-sky-700">
              <Settings size={16} />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{title}</h2>
              <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
            </div>
          </div>
          <div className="shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-600">
            {progress.answeredQuestions || 0} / {progress.totalQuestions || questionCount} answered
          </div>
        </div>
      </div>

      <div className="space-y-5 p-4">
        {profileSetup?.dataCoverage && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Data coverage</h3>
              <span className="text-xs font-medium text-slate-500">{progress.completionPercentage || 0}% questions complete</span>
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
              {Object.entries(profileSetup.dataCoverage.datapoints || {}).map(([key, present]) => (
                <div key={key} className={`rounded-md border px-2.5 py-2 text-xs font-medium ${present ? 'border-emerald-100 bg-white text-emerald-700' : 'border-slate-200 bg-white text-slate-500'}`}>
                  {fieldLabel(key)}
                </div>
              ))}
            </div>
          </div>
        )}

        {showFinished ? (
          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-emerald-200 bg-white text-emerald-600">
                  <CheckCircle2 size={18} />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Matching profile</p>
                  <h3 className="mt-1 font-display text-xl font-normal text-[#0b1f3a]">Questionnaire saved</h3>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
                    Job-side matching context is ready for deterministic scoring, candidate explanations, and publish-time matching.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  onFinishedChange(false);
                  onActiveIndexChange(0);
                }}
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:border-sky-200 hover:text-sky-700"
              >
                Review answers
              </button>
            </div>
            <div className="mt-5 overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="grid sm:grid-cols-3">
                <div className="border-b border-slate-100 px-4 py-3 sm:border-b-0 sm:border-r">
                  <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Answered</div>
                  <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{progress.answeredQuestions || 0}</div>
                </div>
                <div className="border-b border-slate-100 px-4 py-3 sm:border-b-0 sm:border-r">
                  <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Skipped</div>
                  <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{progress.skippedQuestions || 0}</div>
                </div>
                <div className="px-4 py-3">
                  <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">Completion</div>
                  <div className="mt-1 font-display text-2xl font-normal text-[#0b1f3a]">{progress.completionPercentage || 0}%</div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
                    <span className="text-[#0b1f3a]">
                      {progress.answeredQuestions || 0} of {progress.totalQuestions || questionCount} answered
                    </span>
                    <span>{progress.completionPercentage || 0}% complete</span>
                  </div>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-white ring-1 ring-slate-200">
                    <div
                      className="h-full rounded-full bg-sky-500 transition-all"
                      style={{ width: `${progress.completionPercentage || 0}%` }}
                    />
                  </div>
                </div>
                <div className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-[#0b1f3a]">
                  Question {safeIndex + 1} / {questionCount}
                </div>
              </div>
              <div className="mt-3 border-t border-slate-200 pt-3">
                <span className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">Questions</span>
                <div className="mt-3 flex flex-wrap gap-2">
                  {questions.map((question, index) => {
                    const status = getQuestionStatus(question, {
                      answers,
                      skippedAnswers,
                      dirtyQuestionKeys,
                      recentlySavedQuestionKeys,
                      savingQuestionKey
                    });
                    const active = index === safeIndex;
                    const statusClass = active
                      ? 'border-[#0b1f3a] bg-[#0b1f3a] text-white'
                      : status === 'saved'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : status === 'draft'
                          ? 'border-amber-200 bg-amber-50 text-amber-700'
                          : status === 'skipped'
                            ? 'border-slate-200 bg-slate-100 text-slate-500'
                            : 'border-slate-200 bg-white text-slate-500 hover:border-sky-200 hover:text-sky-700';

                    return (
                      <button
                        key={question.questionKey}
                        type="button"
                        onClick={() => goToQuestion(index)}
                        disabled={Boolean(savingQuestionKey)}
                        className={`flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${statusClass}`}
                        aria-label={`Go to question ${index + 1}`}
                      >
                        {status === 'saving' ? <Loader2 size={14} className="animate-spin" /> : index + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <section className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-4 sm:p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-slate-400">{currentSection}</p>
                    {currentQuestion.required && (
                      <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-600">Required by bank</span>
                    )}
                  </div>
                  <h3 className="mt-2 break-words font-display text-xl font-normal leading-7 text-[#0b1f3a]">
                    {currentQuestion.prompt}
                  </h3>
                  {currentQuestion.helpText && (
                    <p className="mt-2 break-words text-sm leading-6 text-slate-500">{currentQuestion.helpText}</p>
                  )}
                </div>
                <label className="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-500">
                  <input
                    type="checkbox"
                    checked={Boolean(skippedAnswers[currentKey])}
                    disabled={currentIsSaving}
                    onChange={(event) => onSkipChange(currentKey, event.target.checked)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  Skip
                </label>
              </div>

              <div className="mt-6">
                {renderQuestionInput(currentQuestion)}
                {errors[currentKey] && <p className="mt-2 text-xs font-medium text-rose-600">{errors[currentKey]}</p>}
              </div>

              <div className="mt-6 flex flex-col gap-3 border-t border-slate-200 pt-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="text-xs font-medium text-slate-500">
                  {currentIsSaving ? 'Saving...' : currentStatus === 'saved' ? 'Saved' : currentStatus === 'skipped' ? 'Skipped' : currentStatus === 'draft' ? 'Unsaved changes' : 'Not answered'}
                </div>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => goToQuestion(safeIndex - 1)}
                    disabled={!hasPrevious || currentIsSaving}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ChevronLeft size={16} /> Back
                  </button>
                  <button
                    type="button"
                    onClick={() => onSaveQuestion(currentQuestion, {
                      advance: hasNext,
                      finish: !hasNext
                    })}
                    disabled={Boolean(savingQuestionKey)}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345c] disabled:cursor-not-allowed disabled:opacity-65"
                  >
                    {currentIsSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {hasNext ? 'Save and next' : 'Save and finish'}
                  </button>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </section>
  );
};

export default JobProfileQuestionStepper;
